import React, { useEffect, useState, useRef } from 'react';
import { FormField } from './FormField';
import { LockIcon, CreditCardIcon, MailIcon, KeyIcon, FileTextIcon, DollarSignIcon, LinkIcon, SmartphoneIcon, BellIcon, FileIcon, ChevronDownIcon, ChevronRightIcon, CreditCardIcon as SavedCardIcon, GlobeIcon, SearchIcon, XIcon, CheckIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface StandardPaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
  onSecurityTypeChange?: (securityType: 'public' | 'password' | 'email' | 'sms' | 'document' | 'geo') => void;
}
export function StandardPayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSecurityTypeChange
}: StandardPaymentProps) {
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [reference, setReference] = useState('');
  const [storePaymentMethod, setStorePaymentMethod] = useState(false);
  const [selectedPaymentProviders, setSelectedPaymentProviders] = useState<string[]>([]);
  const [securityOption, setSecurityOption] = useState<'public' | 'password' | 'email' | 'sms' | 'document' | 'geo'>('public');
  const [securityEmail, setSecurityEmail] = useState('');
  const [securityPhone, setSecurityPhone] = useState('');
  const [securityPassword, setSecurityPassword] = useState('');
  const [documentType, setDocumentType] = useState('government-id');
  const [customDocumentName, setCustomDocumentName] = useState('');
  const [customDocumentDescription, setCustomDocumentDescription] = useState('');
  const [securitySectionExpanded, setSecuritySectionExpanded] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [countrySearchQuery, setCountrySearchQuery] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryDropdownRef = useRef<HTMLDivElement>(null);
  // Comprehensive list of countries with flags
  const countries = [{
    code: 'US',
    name: 'United States',
    flag: '🇺🇸'
  }, {
    code: 'CA',
    name: 'Canada',
    flag: '🇨🇦'
  }, {
    code: 'GB',
    name: 'United Kingdom',
    flag: '🇬🇧'
  }, {
    code: 'AU',
    name: 'Australia',
    flag: '🇦🇺'
  }, {
    code: 'DE',
    name: 'Germany',
    flag: '🇩🇪'
  }, {
    code: 'FR',
    name: 'France',
    flag: '🇫🇷'
  }, {
    code: 'JP',
    name: 'Japan',
    flag: '🇯🇵'
  }, {
    code: 'CN',
    name: 'China',
    flag: '🇨🇳'
  }, {
    code: 'IN',
    name: 'India',
    flag: '🇮🇳'
  }, {
    code: 'BR',
    name: 'Brazil',
    flag: '🇧🇷'
  }, {
    code: 'MX',
    name: 'Mexico',
    flag: '🇲🇽'
  }, {
    code: 'ES',
    name: 'Spain',
    flag: '🇪🇸'
  }, {
    code: 'IT',
    name: 'Italy',
    flag: '🇮🇹'
  }, {
    code: 'NL',
    name: 'Netherlands',
    flag: '🇳🇱'
  }, {
    code: 'SE',
    name: 'Sweden',
    flag: '🇸🇪'
  }, {
    code: 'NO',
    name: 'Norway',
    flag: '🇳🇴'
  }, {
    code: 'DK',
    name: 'Denmark',
    flag: '🇩🇰'
  }, {
    code: 'FI',
    name: 'Finland',
    flag: '🇫🇮'
  }, {
    code: 'SG',
    name: 'Singapore',
    flag: '🇸🇬'
  }, {
    code: 'AE',
    name: 'United Arab Emirates',
    flag: '🇦🇪'
  }, {
    code: 'AR',
    name: 'Argentina',
    flag: '🇦🇷'
  }, {
    code: 'AT',
    name: 'Austria',
    flag: '🇦🇹'
  }, {
    code: 'BE',
    name: 'Belgium',
    flag: '🇧🇪'
  }, {
    code: 'CL',
    name: 'Chile',
    flag: '🇨🇱'
  }, {
    code: 'CO',
    name: 'Colombia',
    flag: '🇨🇴'
  }, {
    code: 'CZ',
    name: 'Czech Republic',
    flag: '🇨🇿'
  }, {
    code: 'EG',
    name: 'Egypt',
    flag: '🇪🇬'
  }, {
    code: 'GR',
    name: 'Greece',
    flag: '🇬🇷'
  }, {
    code: 'HK',
    name: 'Hong Kong',
    flag: '🇭🇰'
  }, {
    code: 'HU',
    name: 'Hungary',
    flag: '🇭🇺'
  }, {
    code: 'ID',
    name: 'Indonesia',
    flag: '🇮🇩'
  }, {
    code: 'IE',
    name: 'Ireland',
    flag: '🇮🇪'
  }, {
    code: 'IL',
    name: 'Israel',
    flag: '🇮🇱'
  }, {
    code: 'KR',
    name: 'South Korea',
    flag: '🇰🇷'
  }, {
    code: 'MY',
    name: 'Malaysia',
    flag: '🇲🇾'
  }, {
    code: 'NG',
    name: 'Nigeria',
    flag: '🇳🇬'
  }, {
    code: 'NZ',
    name: 'New Zealand',
    flag: '🇳🇿'
  }, {
    code: 'PH',
    name: 'Philippines',
    flag: '🇵🇭'
  }, {
    code: 'PL',
    name: 'Poland',
    flag: '🇵🇱'
  }, {
    code: 'PT',
    name: 'Portugal',
    flag: '🇵🇹'
  }, {
    code: 'RO',
    name: 'Romania',
    flag: '🇷🇴'
  }, {
    code: 'RU',
    name: 'Russia',
    flag: '🇷🇺'
  }, {
    code: 'SA',
    name: 'Saudi Arabia',
    flag: '🇸🇦'
  }, {
    code: 'CH',
    name: 'Switzerland',
    flag: '🇨🇭'
  }, {
    code: 'TH',
    name: 'Thailand',
    flag: '🇹🇭'
  }, {
    code: 'TR',
    name: 'Turkey',
    flag: '🇹🇷'
  }, {
    code: 'TW',
    name: 'Taiwan',
    flag: '🇹🇼'
  }, {
    code: 'UA',
    name: 'Ukraine',
    flag: '🇺🇦'
  }, {
    code: 'VN',
    name: 'Vietnam',
    flag: '🇻🇳'
  }, {
    code: 'ZA',
    name: 'South Africa',
    flag: '🇿🇦'
  }];
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target as Node)) {
        setShowCountryDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const filteredCountries = countrySearchQuery ? countries.filter(country => country.name.toLowerCase().includes(countrySearchQuery.toLowerCase()) || country.code.toLowerCase().includes(countrySearchQuery.toLowerCase())) : countries;
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    if (onExpiryDateChange) {
      onExpiryDateChange(date);
    }
  };
  const handleAmountChange = (newAmount: string) => {
    setAmount(newAmount);
    if (onAmountChange) {
      onAmountChange(newAmount);
    }
  };
  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
    if (onCurrencyChange) {
      onCurrencyChange(newCurrency);
    }
  };
  const handleReferenceChange = (newReference: string) => {
    setReference(newReference);
    if (onReferenceChange) {
      onReferenceChange(newReference);
    }
  };
  const handleSecurityOptionChange = (newOption: 'public' | 'password' | 'email' | 'sms' | 'document' | 'geo') => {
    setSecurityOption(newOption);
    if (onSecurityTypeChange) {
      onSecurityTypeChange(newOption);
    }
  };
  const toggleCountrySelection = (countryCode: string) => {
    if (selectedCountries.includes(countryCode)) {
      setSelectedCountries(selectedCountries.filter(code => code !== countryCode));
    } else {
      setSelectedCountries([...selectedCountries, countryCode]);
    }
  };
  const removeSelectedCountry = (countryCode: string) => {
    setSelectedCountries(selectedCountries.filter(code => code !== countryCode));
  };
  const getCountryByCode = (code: string) => {
    return countries.find(country => country.code === code);
  };
  return <div className="space-y-6" data-id="element-1327">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1328">
        <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-1329">
          <DollarSignIcon size={18} className="mr-2 text-blue-600" data-id="element-1330" />
          Single payment
        </h2>
        <FormField label="Payment amount" required tooltip="The amount your customer will be charged" data-id="element-1331">
          <div className="relative" data-id="element-1332">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1333">
              <span className="text-gray-500" data-id="element-1334">$</span>
            </div>
            <input type="text" className="block w-full pl-8 pr-12 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" value={amount} onChange={e => handleAmountChange(e.target.value)} data-id="element-1335" />
            <div className="absolute inset-y-0 right-0 flex items-center" data-id="element-1336">
              <select className="h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-500 sm:text-sm rounded-md focus:ring-blue-500 focus:border-blue-500" value={currency} onChange={e => handleCurrencyChange(e.target.value)} data-id="element-1337">
                <option data-id="element-1338">USD</option>
                <option data-id="element-1339">EUR</option>
                <option data-id="element-1340">GBP</option>
              </select>
            </div>
          </div>
        </FormField>
        <FormField label="Customer reference" tooltip="Reference for your customer" data-id="element-1341">
          <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Reference ID" value={reference} onChange={e => handleReferenceChange(e.target.value)} data-id="element-1342" />
        </FormField>
        <FormField label="Description" tooltip="A brief description of what this payment is for" data-id="element-1343">
          <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Invoice #1234 or Monthly subscription" data-id="element-1344" />
        </FormField>
        <div className="mt-4" data-id="element-1345">
          <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1346">
            Payment method storage
          </h3>
          <div className="flex items-center justify-between p-3 border border-gray-200 rounded-md" data-id="element-1347">
            <div className="flex-1" data-id="element-1348">
              <div className="flex items-center" data-id="element-1349">
                <SavedCardIcon size={16} className="mr-2 text-blue-600" data-id="element-1350" />
                <span className="font-medium text-gray-800" data-id="element-1351">
                  Store payment method for future use
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-600" data-id="element-1352">
                Customer will be asked to authorize saving their payment method
                for future charges. Only payment methods that support storing
                customer details will be presented
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 mx-1" data-id="element-1353">
                  Debit & Credit Cards
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 mx-1" data-id="element-1354">
                  Alipay
                </span>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 mx-1" data-id="element-1355">
                  WechatPay
                </span>
                .
              </p>
            </div>
            <label className="flex items-center cursor-pointer" data-id="element-1356">
              <input type="checkbox" className="sr-only" checked={storePaymentMethod} onChange={e => setStorePaymentMethod(e.target.checked)} data-id="element-1357" />
              <div className={`w-10 h-5 rounded-full transition-colors ${storePaymentMethod ? 'bg-blue-500' : 'bg-gray-300'} flex items-center p-0.5`} data-id="element-1358">
                <div className={`w-4 h-4 rounded-full bg-white transform transition-transform ${storePaymentMethod ? 'translate-x-5' : ''}`} data-id="element-1359" />
              </div>
            </label>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-1360">
        <button className="w-full text-left flex items-center justify-between text-lg font-medium text-gray-800 mb-2" onClick={() => setSecuritySectionExpanded(!securitySectionExpanded)} data-id="element-1361">
          <div className="flex items-center" data-id="element-1362">
            <LockIcon size={18} className="mr-2 text-blue-600" data-id="element-1363" />
            <span data-id="element-1364">Security settings</span>
          </div>
          {securitySectionExpanded ? <ChevronDownIcon size={20} className="text-gray-500" data-id="element-1365" /> : <ChevronRightIcon size={20} className="text-gray-500" data-id="element-1366" />}
        </button>
        {securityOption !== 'public' && !securitySectionExpanded && <div className="text-sm text-gray-600 ml-7 mt-1" data-id="element-1367">
            {securityOption === 'password' && 'Password protected'}
            {securityOption === 'email' && 'Email verification required'}
            {securityOption === 'sms' && 'SMS verification required'}
            {securityOption === 'document' && 'Document Upload Verification required'}
            {securityOption === 'geo' && `Geo-restricted to ${selectedCountries.length} ${selectedCountries.length === 1 ? 'country' : 'countries'}`}
          </div>}
        {securitySectionExpanded && <div className="mb-6 mt-4" data-id="element-1368">
            <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-1369">
              Secure access method
            </h4>
            <div className="space-y-4" data-id="element-1370">
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-1371">
                <label className="flex items-start cursor-pointer" data-id="element-1372">
                  <input type="radio" name="securityOption" className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" checked={securityOption === 'public'} onChange={() => handleSecurityOptionChange('public')} data-id="element-1373" />
                  <div className="ml-3" data-id="element-1374">
                    <div className="flex items-center" data-id="element-1375">
                      <LinkIcon size={16} className="mr-2 text-blue-500" data-id="element-1376" />
                      <span className="font-medium text-gray-800" data-id="element-1377">
                        Public access
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-1378">
                      Everyone with the link can see and pay. No additional
                      security required. This is the simplest option for sharing
                      your payment link.
                    </p>
                  </div>
                </label>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-1379">
                <label className="flex items-start cursor-pointer" data-id="element-1380">
                  <input type="radio" name="securityOption" className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" checked={securityOption === 'password'} onChange={() => handleSecurityOptionChange('password')} data-id="element-1381" />
                  <div className="ml-3" data-id="element-1382">
                    <div className="flex items-center" data-id="element-1383">
                      <KeyIcon size={16} className="mr-2 text-blue-500" data-id="element-1384" />
                      <span className="font-medium text-gray-800" data-id="element-1385">
                        Password protection
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-1386">
                      Set a password that you'll need to share with your
                      customer directly. They'll need to enter this password to
                      access the payment page.
                    </p>
                    {securityOption === 'password' && <div className="mt-3" data-id="element-1387">
                        <input type="password" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Enter a secure password" value={securityPassword} onChange={e => setSecurityPassword(e.target.value)} data-id="element-1388" />
                      </div>}
                  </div>
                </label>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-1389">
                <label className="flex items-start cursor-pointer" data-id="element-1390">
                  <input type="radio" name="securityOption" className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" checked={securityOption === 'email'} onChange={() => handleSecurityOptionChange('email')} data-id="element-1391" />
                  <div className="ml-3" data-id="element-1392">
                    <div className="flex items-center" data-id="element-1393">
                      <MailIcon size={16} className="mr-2 text-blue-500" data-id="element-1394" />
                      <span className="font-medium text-gray-800" data-id="element-1395">
                        Email verification
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-1396">
                      Restrict access to a specific email address. The customer
                      will receive a verification code via email and only they
                      will be able to access the payment page.
                    </p>
                    {securityOption === 'email' && <div className="mt-3" data-id="element-1397">
                        <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Enter customer's email address" value={securityEmail} onChange={e => setSecurityEmail(e.target.value)} data-id="element-1398" />
                      </div>}
                  </div>
                </label>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-1399">
                <label className="flex items-start cursor-pointer" data-id="element-1400">
                  <input type="radio" name="securityOption" className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" checked={securityOption === 'sms'} onChange={() => handleSecurityOptionChange('sms')} data-id="element-1401" />
                  <div className="ml-3" data-id="element-1402">
                    <div className="flex items-center" data-id="element-1403">
                      <SmartphoneIcon size={16} className="mr-2 text-blue-500" data-id="element-1404" />
                      <span className="font-medium text-gray-800" data-id="element-1405">
                        SMS verification
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-1406">
                      Restrict access to a specific phone number. The customer
                      will receive a verification code via SMS and only they
                      will be able to access the payment page.
                    </p>
                    {securityOption === 'sms' && <div className="mt-3" data-id="element-1407">
                        <input type="tel" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Enter customer's phone number" value={securityPhone} onChange={e => setSecurityPhone(e.target.value)} data-id="element-1408" />
                      </div>}
                  </div>
                </label>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-1409">
                <label className="flex items-start cursor-pointer" data-id="element-1410">
                  <input type="radio" name="securityOption" className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" checked={securityOption === 'geo'} onChange={() => handleSecurityOptionChange('geo')} data-id="element-1411" />
                  <div className="ml-3 w-full" data-id="element-1412">
                    <div className="flex items-center" data-id="element-1413">
                      <GlobeIcon size={16} className="mr-2 text-blue-500" data-id="element-1414" />
                      <span className="font-medium text-gray-800" data-id="element-1415">
                        Geographic restriction
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-1416">
                      Restrict access to customers physically located in
                      specific countries. Access will be determined by the
                      customer's IP address, ensuring they must be physically
                      present in one of the selected countries to access the
                      payment link.
                    </p>
                    {securityOption === 'geo' && <div className="mt-3" data-id="element-1417">
                        <div className="relative" ref={countryDropdownRef} data-id="element-1418">
                          <div className="flex flex-wrap gap-2 mb-2" data-id="element-1419">
                            {selectedCountries.map(countryCode => {
                        const country = getCountryByCode(countryCode);
                        return <div key={countryCode} className="bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm flex items-center" data-id="element-1420">
                                  <span className="mr-1" data-id="element-1421">{country?.flag}</span>
                                  <span data-id="element-1422">{country?.name}</span>
                                  <button onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeSelectedCountry(countryCode);
                          }} className="ml-1 text-blue-600 hover:text-blue-800" data-id="element-1423">
                                    <XIcon size={14} data-id="element-1424" />
                                  </button>
                                </div>;
                      })}
                          </div>
                          <div className="relative" data-id="element-1425">
                            <input type="text" className="block w-full pl-10 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search for countries..." value={countrySearchQuery} onChange={e => setCountrySearchQuery(e.target.value)} onFocus={() => setShowCountryDropdown(true)} data-id="element-1426" />
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1427">
                              <SearchIcon size={16} className="text-gray-400" data-id="element-1428" />
                            </div>
                          </div>
                          {showCountryDropdown && <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto" data-id="element-1429">
                              {filteredCountries.length > 0 ? filteredCountries.map(country => <div key={country.code} className={`px-4 py-2 hover:bg-gray-100 cursor-pointer flex justify-between items-center ${selectedCountries.includes(country.code) ? 'bg-blue-50' : ''}`} onClick={() => toggleCountrySelection(country.code)} data-id="element-1430">
                                    <div className="flex items-center" data-id="element-1431">
                                      <span className="mr-2" data-id="element-1432">
                                        {country.flag}
                                      </span>
                                      <span data-id="element-1433">{country.name}</span>
                                    </div>
                                    {selectedCountries.includes(country.code) && <CheckIcon size={16} className="text-blue-600" data-id="element-1434" />}
                                  </div>) : <div className="px-4 py-2 text-gray-500" data-id="element-1435">
                                  No countries found
                                </div>}
                            </div>}
                        </div>
                        <div className="flex justify-between mt-2" data-id="element-1436">
                          <button type="button" className="text-sm text-blue-600 hover:text-blue-800" onClick={() => {
                      setShowCountryDropdown(false);
                      setCountrySearchQuery('');
                    }} data-id="element-1437">
                            Done
                          </button>
                          {selectedCountries.length > 0 && <button type="button" className="text-sm text-gray-600 hover:text-gray-800" onClick={() => setSelectedCountries([])} data-id="element-1438">
                              Clear all
                            </button>}
                        </div>
                        {selectedCountries.length === 0 && <p className="mt-2 text-sm text-yellow-600 flex items-start" data-id="element-1439">
                            <BellIcon size={14} className="mr-1 mt-0.5 flex-shrink-0" data-id="element-1440" />
                            Please select at least one country to enable
                            geographic restriction.
                          </p>}
                      </div>}
                  </div>
                </label>
              </div>
              <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors" data-id="element-1441">
                <label className="flex items-start cursor-pointer" data-id="element-1442">
                  <input type="radio" name="securityOption" className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300" checked={securityOption === 'document'} onChange={() => handleSecurityOptionChange('document')} data-id="element-1443" />
                  <div className="ml-3" data-id="element-1444">
                    <div className="flex items-center" data-id="element-1445">
                      <FileTextIcon size={16} className="mr-2 text-blue-500" data-id="element-1446" />
                      <span className="font-medium text-gray-800" data-id="element-1447">
                        Document Upload Verification
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600" data-id="element-1448">
                      Customer will need to upload a specific document to unlock
                      the payment link. You'll be notified when they upload the
                      document and will need to review and approve it before
                      they can proceed with the payment.
                    </p>
                    {securityOption === 'document' && <div className="mt-3 p-3 bg-gray-50 rounded-md" data-id="element-1449">
                        <div className="flex items-start mb-3 p-3 bg-blue-50 border border-blue-100 rounded-md" data-id="element-1450">
                          <BellIcon size={16} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-1451" />
                          <p className="text-sm text-blue-700" data-id="element-1452">
                            You'll receive a notification when the customer
                            uploads a document. You must review and approve it
                            from your dashboard before the customer can access
                            the payment page.
                          </p>
                        </div>
                        <div className="text-sm font-medium text-gray-700 mb-2" data-id="element-1453">
                          Select required document type:
                        </div>
                        <div className="space-y-2" data-id="element-1454">
                          <label className="flex items-center" data-id="element-1455">
                            <input type="radio" name="documentType" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={documentType === 'government-id'} onChange={() => setDocumentType('government-id')} data-id="element-1456" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-1457">
                              Government-issued ID (Passport, Driver's License)
                            </span>
                          </label>
                          <label className="flex items-center" data-id="element-1458">
                            <input type="radio" name="documentType" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={documentType === 'proof-address'} onChange={() => setDocumentType('proof-address')} data-id="element-1459" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-1460">
                              Proof of address (Utility bill, Bank statement)
                            </span>
                          </label>
                          <label className="flex items-center" data-id="element-1461">
                            <input type="radio" name="documentType" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={documentType === 'business-verification'} onChange={() => setDocumentType('business-verification')} data-id="element-1462" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-1463">
                              Business verification (Business license, Tax ID)
                            </span>
                          </label>
                          <label className="flex items-center" data-id="element-1464">
                            <input type="radio" name="documentType" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" checked={documentType === 'custom'} onChange={() => setDocumentType('custom')} data-id="element-1465" />
                            <span className="ml-2 text-sm text-gray-700" data-id="element-1466">
                              Custom document
                            </span>
                          </label>
                          {documentType === 'custom' && <div className="mt-3 pl-6 space-y-3" data-id="element-1467">
                              <div data-id="element-1468">
                                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1469">
                                  Document name
                                </label>
                                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Proof of insurance" value={customDocumentName} onChange={e => setCustomDocumentName(e.target.value)} data-id="element-1470" />
                              </div>
                              <div data-id="element-1471">
                                <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1472">
                                  Instructions for customer
                                </label>
                                <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Explain what document you need and any specific requirements" rows={3} value={customDocumentDescription} onChange={e => setCustomDocumentDescription(e.target.value)} data-id="element-1473" />
                              </div>
                              <div className="flex items-center text-xs text-gray-500" data-id="element-1474">
                                <FileIcon size={12} className="mr-1 text-gray-400" data-id="element-1475" />
                                These instructions will be shown to the customer
                                on the payment page
                              </div>
                            </div>}
                        </div>
                      </div>}
                  </div>
                </label>
              </div>
            </div>
          </div>}
      </div>
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} selectedPaymentProviders={selectedPaymentProviders} onSelectedPaymentProvidersChange={setSelectedPaymentProviders} data-id="element-1476" />
    </div>;
}